// Voorinstellingen van "Het klaslokaal in de ochtend": zeven lichte, twee donkere. Papier is de standaard.
// Elke set haalt WCAG AA voor tekst én accent op het voorgrondvlak (zie theme.test.ts).
export type ThemeScope = 'site' | 'planner';

export type ThemeInput = {
  presetKey: string;
  /** null = plain colour, "preset:<key>" = built-in /backgrounds/<key>.svg, "upload" = the teacher's own image. */
  background: string | null;
  foregroundColor: string;
  foregroundOpacity: number;
  textColor: string;
  accentColor: string;
  fontHeading: string;
  fontBody: string;
  /** Gives the sheet title a stroke in the panel colour, so it stays readable over a busy border. */
  titleOutline: boolean;
};

export type Theme = ThemeInput & {
  scope: ThemeScope;
  /** Planner only: no theme of its own yet, it shows the website theme. */
  inherited: boolean;
  backgroundVersion: string | null;
  hasLogo: boolean;
  logoVersion: string | null;
};

export type Preset = { key: string; label: string; note: string; values: ThemeInput };

const preset = (key: string, label: string, note: string, values: Omit<ThemeInput, 'presetKey' | 'background' | 'titleOutline'>): Preset => ({
  key,
  label,
  note,
  values: { presetKey: key, background: `preset:${key}`, titleOutline: false, ...values },
});

export const PRESETS: Preset[] = [
  preset('papier', 'Papier', 'Warm wit, blauwgroen', {
    foregroundColor: '#ffffff', foregroundOpacity: 94, textColor: '#2b2f36', accentColor: '#0f766e',
    fontHeading: 'nunito', fontBody: 'source-sans-3',
  }),
  preset('zomerklas', 'Zomerklas', 'Zonlicht, koraal', {
    foregroundColor: '#fff7e6', foregroundOpacity: 94, textColor: '#243044', accentColor: '#c2391f',
    fontHeading: 'fredoka', fontBody: 'source-sans-3',
  }),
  preset('lente', 'Lente', 'Fris groen, gras', {
    foregroundColor: '#f5fbf0', foregroundOpacity: 94, textColor: '#1f3b2a', accentColor: '#2e7d32',
    fontHeading: 'nunito', fontBody: 'source-sans-3',
  }),
  preset('zee', 'Zee', 'Lichtblauw, diepzee', {
    foregroundColor: '#eef6fb', foregroundOpacity: 94, textColor: '#16324a', accentColor: '#0b6e99',
    fontHeading: 'nunito', fontBody: 'source-sans-3',
  }),
  preset('krijt', 'Krijt', 'Grijsblauw, inkt', {
    foregroundColor: '#eef1f5', foregroundOpacity: 94, textColor: '#26303a', accentColor: '#3b5b8c',
    fontHeading: 'nunito', fontBody: 'source-sans-3',
  }),
  preset('perzik', 'Perzik', 'Zacht oranje, terracotta', {
    foregroundColor: '#fff1e8', foregroundOpacity: 94, textColor: '#3a2a24', accentColor: '#b8432a',
    fontHeading: 'nunito', fontBody: 'source-sans-3',
  }),
  preset('mint', 'Mint', 'Mintgroen, zeegroen', {
    foregroundColor: '#ecf8f3', foregroundOpacity: 94, textColor: '#1e3a30', accentColor: '#0f7a5c',
    fontHeading: 'nunito', fontBody: 'source-sans-3',
  }),
  preset('schoolbord', 'Schoolbord', 'Donker krijtbord, krijtgeel', {
    foregroundColor: '#1d2b27', foregroundOpacity: 86, textColor: '#eef2e8', accentColor: '#f2d16b',
    fontHeading: 'caveat', fontBody: 'source-sans-3',
  }),
  preset('bosgroen', 'Bosgroen', 'Linnen, mos (dark academia)', {
    foregroundColor: '#1f2e24', foregroundOpacity: 90, textColor: '#ecefe0', accentColor: '#c9b26a',
    fontHeading: 'eb-garamond', fontBody: 'lora',
  }),
];

export const DEFAULT_PRESET: Preset = PRESETS[0] as Preset;

export const DEFAULT_THEME: Theme = {
  ...DEFAULT_PRESET.values,
  scope: 'site',
  inherited: false,
  backgroundVersion: null,
  hasLogo: false,
  logoVersion: null,
};
